import type { Logger } from '@feature-hub/logger';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

interface ContextState {
  readonly loggerService?: Logger;
  readonly vueFormatterService: VueFormatterServiceInterfaceV1;
  readonly localeService: LocaleServiceV1;
  readonly trackingService?: TrackingServiceV2;
  readonly contentService: ContentServiceV1;
}

export const Context = createContext<ContextState>({} as ContextState);

export interface ContextProviderProps {
  readonly localeService: LocaleServiceV1;
  readonly vueFormatterService: VueFormatterServiceInterfaceV1;
  readonly contentService: ContentServiceV1;
  readonly logger?: Logger;
  readonly trackingService?: TrackingServiceV2;
}

const ContextProvider = ({
  children,
  vueFormatterService,
  localeService,
  logger,
  trackingService,
  contentService,
}: PropsWithChildren<ContextProviderProps>) => {
  const value = useMemo(
    () => ({
      loggerService: logger,
      vueFormatterService,
      localeService,
      trackingService,
      contentService,
    }),
    [],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default ContextProvider;

function getContext() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ctx = useContext(Context);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (ctx === null) {
    throw new Error(
      'Context not initialized. Did you forget to wrap the Feature App in <ContextProvider>?',
    );
  }
  return ctx;
}

export function useLogger() {
  return getContext().loggerService;
}
